/**
 * Runs the first batch load for the external PA orders table using persisted context and filters.
 */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../../context/tablesContextProvider";
import { RootState } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { makeAlgoliaCallForInitialDataFetch } from "../apis/makeAlgoliaCallForInitialDataFetch";
import { generateFilterPayloadForExternalOrders } from "../utils/generateFilterPayloadForExternalOrders";
import { generatePayloadForInitialDataFetch } from "../utils/generatePayloadForInitialDataFetch";
import { ExternalTableState } from "./useExternalTableData";
import { useExternalValidFilters } from "./useExternalValidFilters";

const EXTERNAL_TABLE_NAME = "allMedicalPaOrdersForExternalByAlgolia";

/**
 * Manages the initial data fetch for the external PA orders table on mount.
 * Generates payloads from persisted table context (search text, last batch) and valid filters,
 * then triggers the batch fetch once auth status options are available.
 *
 * @param setExternalTableState - State setter for orders loading/data lifecycle
 * @param setTableData - State setter for table row data
 * @returns Whether the initial fetch has completed, and a setter to re-trigger it
 */
export const useInitialDataFetchForExternalTable = (
  setExternalTableState: React.Dispatch<
    React.SetStateAction<ExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState(false);

  const orgId = getOrgIdForFetchExternalWorklist();
  const { getTableDataFromContext } = useTablesContext();
  const tableAttributes = getTableDataFromContext(EXTERNAL_TABLE_NAME);
  const validFilters = useExternalValidFilters();

  useEffect(() => {
    if (
      !initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      const payload = generateFilterPayloadForExternalOrders(
        validFilters,
        tableAttributes?.searchedText ?? "",
        orgId,
      );
      const payloads = generatePayloadForInitialDataFetch(
        payload,
        tableAttributes?.lastBatchFetched ?? 0,
        tableAttributes?.searchedText ?? "",
      );

      makeAlgoliaCallForInitialDataFetch(
        payloads,
        authStatusOptions,
        setExternalTableState,
        setTableData,
        setInitialDataFetchingExecuted,
      );
    }
  }, [authStatusOptions, initialDataFetchingExecuted]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
